import { useState, useEffect } from 'react';
import Select from 'react-select';
import Loader from '../../../components/globalloaders/Loader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, EllipsisVerticalIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { agreementStatusOptions, agreementValidOptions, categoryOptions } from '../../../constants/options';
import { formatLabel, encryptPayload, decryptPayload, formatTableDateTime, formatDateTime, toDateTimeLocal, toDateTimeIso, formatCurrency } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import FallBack from '../../../components/dialogbox/FallBack';
import api from '../../../utils/auth/api';
import { PDFDocument, degrees, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';

const Agreement = ({ setIsMobileOpen }) => {

  const mediaURI = `${process.env.REACT_APP_URI}/stream/internal-media`;

  const { isLoading, setIsLoading } = useLoading();
  const [loaders, setLoaders] = useState({
    fetch: false,
    update: false,
    details: false,
    search: false,
    pdfFilter: false,
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "",
    action: () => { },
  });
  const [opCodeDialog, setOpCodeDialog] = useState({
    open: false,
    id: null,
  });

  const [entryData, setEntryData] = useState([]);

  // Menu List State
  const [menuState, setMenuState] = useState({
    open: false,
    rowId: null,
    rowData: null,
    position: { top: 0, left: 0 },
  });
  const openMenu = ({ id, position, row }) => {
    setMenuState({
      open: true,
      rowId: id,
      rowData: row,
      position,
    });
  };
  const closeMenu = () => {
    setMenuState({
      open: false,
      rowId: null,
      rowData: null,
      position: { top: 0, left: 0 },
    });
  };

  // For Search Quick & Deep
  const [quickResults, setQuickResults] = useState([]);
  const [deepResults, setDeepResults] = useState([]);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState("quick");
  const [isQuickSearching, setIsQuickSearching] = useState(false);
  const [totalBackendPg, setTotalBackendPg] = useState(1);
  const getDisplayData = () => {
    if (searchMode === "quick") {
      return isQuickSearching ? quickResults : entryData;
    }
    if (searchMode === "deep") {
      return deepResults;
    }
    return entryData;
  };

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const displayData = getDisplayData();
  const [totalPages, setTotalPages] = useState(1);
  const paginatedData = displayData;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);
  useEffect(() => {
    setQuery("");
    setQuickResults([]);
    setDeepResults([]);
    setIsQuickSearching(false);
    if (searchMode === "quick") {
      setCurrentPage(1);
      setTotalPages(Math.ceil(totalBackendPg / itemsPerPage));
    } else if (searchMode === "deep") {
      setCurrentPage(1);
      setTotalPages(1);
    }
  }, [searchMode, totalBackendPg, itemsPerPage]);

  // Details DialogBox with Api Call By ID 
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [formData1, setFormData1] = useState({});
  const handleViewOpen = async (id) => {
    setIsViewOpen(true);
    setLoaders(prev => ({ ...prev, details: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setFormData1(decryptedData);
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Agreement details:', error);
    } finally {
      setLoaders(prev => ({ ...prev, details: false }));
      return;
    }
  };
  const handleViewClose = () => {
    setIsViewOpen(false);
    setFormData1({});
  };

  // Update DialogBox with Api Call and Send
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [formData2, setFormData2] = useState({
    id: '',
    category: '',
    agreementDate: '',
    validFor: '',
    refNo: '',
    salary: '',
    commission: '',
    status: '',
  });
  const handleSelectUpdate = (selectedOption, { name }) => {
    setFormData2(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };
  const handleChangeUpdate = (e) => {
    setFormData2(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const digits = value.replace(/\D/g, "");
    setFormData2(prev => ({ ...prev, [name]: digits }));
  };
  const handleUpdateOpen = async (id) => {
    setIsUpdateOpen(true);
    setLoaders(prev => ({ ...prev, update: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setFormData2({
          id,
          category: decryptedData?.category || '',
          agreementDate: toDateTimeLocal(decryptedData?.agreementDate) || '',
          validFor: decryptedData?.validFor || '',
          refNo: decryptedData?.refNo || '',
          salary: decryptedData?.salary || '',
          commission: decryptedData?.commission || '',
          status: decryptedData?.status || '',
        });
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Agreement edit:', error);
    } finally {
      setLoaders(prev => ({ ...prev, update: false }));
      return;
    }
  };
  const handleUpdateClose = () => {
    setIsUpdateOpen(false);
    setFormData2({
      id: '',
      category: '',
      agreementDate: '',
      validFor: '',
      refNo: '',
      salary: '',
      commission: '',
      status: '',
    });
  };
  // Update API
  const handleUpdate = async (id) => {
    if (isLoading) return;

    if (!id) {
      toast.error('Unable to update: ID is missing');
      return;
    };
    if (!formData2.category) {
      toast.warning("Category is required.");
      return;
    }
    if (!formData2.agreementDate) {
      toast.warning("Date is required.");
      return;
    }
    if (!formData2.validFor) {
      toast.warning("Valid For is required.");
      return;
    }
    if (!formData2.refNo) {
      toast.warning("Ref No is required.");
      return;
    }
    if (!formData2.salary) {
      toast.warning("Salary is required.");
      return;
    }
    if (!formData2.commission) {
      toast.warning("Commission is required.");
      return;
    }
    if (!formData2.status) {
      toast.warning("Entry Status is required.");
      return;
    }

    const payload = {
      ...formData2,
      salary: Number(formData2.salary),
      commission: Number(formData2.commission),
      agreementDate: toDateTimeIso(formData2.agreementDate),
    };

    setIsLoading(true);

    const encryptedData = encryptPayload(payload);
    try {
      const response = await api.put(`${process.env.REACT_APP_URI}/agreement/update/${id}`, { data: encryptedData });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setEntryData((prevData) => prevData.map((data) => data._id === id ? { ...data, ...decryptedData } : data));
        toast.success(response.data.message);
        handleUpdateClose();
      } else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Agreement Updating Data failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Update Agreement Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // QUICK SEARCH (local state)
  const handleQuickSearch = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (searchMode === "quick") {
      if (value.trim() === "") {
        setIsQuickSearching(false);
        setQuickResults(entryData);
      } else {
        setIsQuickSearching(true);
        const results = entryData.filter((s) =>
          s.employee?.toLowerCase().includes(value.toLowerCase()) ||
          s.client?.toLowerCase().includes(value.toLowerCase()) ||
          s.status?.toLowerCase().includes(value.toLowerCase()) ||
          s.agreementId?.toLowerCase().includes(value.toLowerCase())
        );
        setQuickResults(results);
      }
    }
  };
  // DEEP SEARCH (API)
  const handleDeepSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      toast.warning("Enter value to search");
      return;
    }
    setLoaders(prev => ({ ...prev, search: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/search`, {
        params: { value: searchQuery },
      });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setDeepResults(decryptedData);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error deep searching agreement:", error);
      toast.error(error?.response.data.message || "Deep search agreement failed!");
    } finally {
      setLoaders(prev => ({ ...prev, search: false }));
      return;
    }
  };
  // Connect Search to Quick or Deep via searchMode
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchMode === "quick") {
      handleQuickSearch({ target: { value: query } });
    } else if (searchMode === "deep") {
      handleDeepSearch(query);
    }
  };

  // Fetch All API
  useEffect(() => {
    const fetchData = async (page = 1) => {
      setLoaders(prev => ({ ...prev, fetch: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/agreement/get-all`, {
          params: { page, limit: itemsPerPage },
        });
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setEntryData(decryptedData.data);
          setQuickResults(decryptedData.data);
          setTotalBackendPg(decryptedData.total);
          setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
        }
        else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching agreements:', error);
      } finally {
        setLoaders(prev => ({ ...prev, fetch: false }));
        return;
      }
    };
    fetchData(currentPage);
  }, [currentPage]);

  // Delete API By ID
  const handleDelete = async (id, opcode) => {
    if (isLoading) return;
    setIsLoading(true);
    const payload = encryptPayload({ id, opcode });
    try {
      const response = await api.delete(`${process.env.REACT_APP_URI}/agreement/delete`, { data: { data: payload } });
      if (response.data.success) {
        setEntryData((prevClientData) => prevClientData.filter((client) => client._id !== id));
        toast.success(response.data.message);
      }
    }
    catch (error) {
      console.log("Error occur while deleting agreement:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        toast.error(data.message);
      } else {
        toast.error("Unable to Delete Agreement Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // PDF Generation State, API & Templates
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfFilters, setPdfFilters] = useState({
    month: "",
    year: "",
  });
  const [pdfData, setPdfData] = useState([]);
  const handlePdfOpen = () => {
    setIsPdfOpen(true);
  };
  const handlePdfClose = () => {
    setIsPdfOpen(false);
    setPdfFilters({ month: "", year: "" });
    setPdfData([]);
  };
  const handlePdfFilterChange = (e) => {
    const { name, value } = e.target;
    setPdfFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handlePdfFetch = async () => {
    if (loaders.pdfFilter) return;
    if (!pdfFilters.month || !pdfFilters.year) {
      toast.warning("Select month and year");
      return;
    }
    setLoaders(prev => ({ ...prev, pdfFilter: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/filter-pdf`, { params: { month: pdfFilters.month, year: pdfFilters.year } });
      if (response.data.success) {
        const decrypted = decryptPayload(response.data.encryptedData);
        setPdfData(decrypted);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching pdf agreements data:', error);
      toast.error(error?.response.data.message || "Failed to fetch PDF data!");
    } finally {
      setLoaders(prev => ({ ...prev, pdfFilter: false }));
      return;
    }
  };
  // Monthly Pdf Template
  const handleMonthlyPdf = async () => {
    if (isLoading) return;
    if (!pdfFilters.month || !pdfFilters.year) {
      toast.warning("Select month and year");
      return;
    }
    if (!pdfData || pdfData.length === 0) {
      toast.warning("No data");
      return;
    }
    setIsLoading(true);
    try {
      const customFormattedDate = formatDateTime(new Date().toISOString());
      // Load your PDF template
      const pdfTemplateUrl = '/mbeo-agreementform.pdf';
      const eStampUrl = '/estamp.png';
      const existingPdfBytes = await fetch(pdfTemplateUrl).then((res) => res.arrayBuffer());
      const eStampBytes = await fetch(eStampUrl).then((res) => res.arrayBuffer());

      // Load the base template PDF
      const basePdfDoc = await PDFDocument.load(existingPdfBytes);
      // Create a new PDF document for output
      const pdfDoc = await PDFDocument.create();
      const eStampImage = await pdfDoc.embedPng(eStampBytes);
      const eStampDims = eStampImage.scale(0.27);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      for (const filteredAgreement of pdfData) {
        const [templatePage] = await pdfDoc.copyPages(basePdfDoc, [0]);
        pdfDoc.addPage(templatePage);
        const currentPage = pdfDoc.getPage(pdfDoc.getPageCount() - 1);

        currentPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Agreement ID: ${filteredAgreement.agreementId}`, { x: 150, y: 780, size: 9, color: rgb(0, 0, 0) });

        currentPage.drawText(`${formatLabel(filteredAgreement.category)}`, { x: 85, y: 707, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatLabel(filteredAgreement.validFor)}`, { x: 515, y: 707, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.refNo}`, { x: 80, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatTableDateTime(filteredAgreement.agreementDate)}`, { x: 485, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.clientName}`, { x: 65, y: 610, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.employee?.name}`, { x: 95, y: 552, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.employee?.father_husband_name}`, { x: 85, y: 523, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.clientAddress}`, { x: 125, y: 492, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`PKR ${formatCurrency(filteredAgreement.salary)}`, { x: 65, y: 143, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`PKR ${formatCurrency(filteredAgreement.commission)}`, { x: 480, y: 143, size: 12, color: rgb(0, 0, 0) });

        currentPage.drawText("Agreement ID:", { x: 230, y: 707, size: 9, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawText(`${filteredAgreement.agreementId}`, { x: 310, y: 707, size: 12, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawLine({ start: { x: 290, y: 706 }, end: { x: 380, y: 706 }, thickness: 1, color: rgb(0, 0, 0) });

        currentPage.drawText("Status:", { x: 230, y: 687, size: 9, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatLabel(filteredAgreement.status)}`, { x: 290, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawLine({ start: { x: 260, y: 686 }, end: { x: 380, y: 686 }, thickness: 1, color: rgb(0, 0, 0) });

        currentPage.drawText("Disclaimer: This document is valid only with an Approved stamp and MBEOWS assigned Agreement ID.", { x: 18, y: 8, size: 9, font: boldFont, color: rgb(0, 0, 0) });

        currentPage.drawImage(eStampImage, {
          x: 490,
          y: 10,
          width: eStampDims.width,
          height: eStampDims.height,
        });
      }

      const finalBytes = await pdfDoc.save();
      const blob = new Blob([finalBytes], { type: "application/pdf" });
      saveAs(blob, `Agreements_${pdfFilters.month}_${pdfFilters.year}.pdf`);
      toast.success('PDF Generated');
    } catch (err) {
      console.log("Error generating monthly agreements PDF:", err);
      toast.error("Failed to generate monthly PDF!")
    } finally {
      setIsLoading(false);
      return;
    }
  };

  // Individual Agreement API & Pdf Template
  const getMediaByRole = (media = [], role) => {
    return media.find(m => m.role === role) || null;
  };
  const readMediaAsArrayBuffer = async (url) => {
    if (!url) return null;
    try {
      const res = await api.get(`${mediaURI}${url}`, {
        responseType: "arraybuffer"
      });
      return res.data;
    } catch (err) {
      console.error("Error fetching media for PDF:", err);
      return null;
    }
  };
  const getMimeFromUrl = (url = "") => {
    const u = url.toLowerCase();
    if (u.endsWith(".png")) return "image/png";
    if (u.endsWith(".jpg") || u.endsWith(".jpeg")) return "image/jpeg";
    return "unknown";
  };
  const handleIndividualPdf = async (id) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);

        const customFormattedDate = formatDateTime(new Date().toISOString());
        // Load your PDF template
        const pdfTemplateUrl = '/mbeo-agreementform.pdf';
        const eStampUrl = '/estamp.png';
        const existingPdfBytes = await fetch(pdfTemplateUrl).then((res) => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const eStampBytes = await fetch(eStampUrl).then((res) => res.arrayBuffer());

        const empMedia = decryptedData.employeeRef?.media || [];
        const mediaMap = {
          employeeCnicFront: getMediaByRole(empMedia, "cnic_front"),
          employeeCnicBack: getMediaByRole(empMedia, "cnic_back"),
        };
        const buffers = await Promise.all(
          Object.values(mediaMap).map(m =>
            m ? readMediaAsArrayBuffer(m.url) : Promise.resolve(null)
          )
        );
        const embedSafe = async (buffer, mime) => {
          if (!buffer) return null;
          if (mime === "image/png") return pdfDoc.embedPng(buffer);
          if (mime === "image/jpeg") return pdfDoc.embedJpg(buffer);
          return null;
        };
        const [
          employeeCnicFrontImage,
          employeeCnicBackImage,
        ] = await Promise.all(
          Object.values(mediaMap).map((m, i) =>
            embedSafe(buffers[i], getMimeFromUrl(m?.url))
          )
        );

        const pages = pdfDoc.getPages();
        const eStampImage = await pdfDoc.embedPng(eStampBytes);
        const eStampDims = eStampImage.scale(0.27);
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const currentPage = pages[0];

        currentPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Agreement ID: ${decryptedData.agreementId}`, { x: 150, y: 780, size: 9, color: rgb(0, 0, 0) });

        currentPage.drawText(`${formatLabel(decryptedData.category)}`, { x: 85, y: 707, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatLabel(decryptedData.validFor)}`, { x: 515, y: 707, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.refNo}`, { x: 80, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatTableDateTime(decryptedData.agreementDate)}`, { x: 485, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.clientName}`, { x: 65, y: 610, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.employeeRef?.name}`, { x: 95, y: 552, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.employeeRef?.father_husband_name}`, { x: 85, y: 523, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.clientAddress}`, { x: 125, y: 492, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`PKR ${formatCurrency(decryptedData.salary)}`, { x: 65, y: 143, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawText(`PKR ${formatCurrency(decryptedData.commission)}`, { x: 480, y: 143, size: 12, color: rgb(0, 0, 0) });

        currentPage.drawText("Agreement ID:", { x: 230, y: 707, size: 9, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawText(`${decryptedData.agreementId}`, { x: 310, y: 707, size: 12, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawLine({ start: { x: 290, y: 706 }, end: { x: 380, y: 706 }, thickness: 1, color: rgb(0, 0, 0) });

        currentPage.drawText("Status:", { x: 230, y: 687, size: 9, font: boldFont, color: rgb(0, 0, 0) });
        currentPage.drawText(`${formatLabel(decryptedData.status)}`, { x: 290, y: 687, size: 12, color: rgb(0, 0, 0) });
        currentPage.drawLine({ start: { x: 260, y: 686 }, end: { x: 380, y: 686 }, thickness: 1, color: rgb(0, 0, 0) });

        currentPage.drawText("Disclaimer: This document is valid only with an Approved stamp and MBEOWS assigned Agreement ID.", { x: 18, y: 8, size: 9, font: boldFont, color: rgb(0, 0, 0) });

        currentPage.drawImage(eStampImage, {
          x: 490,
          y: 10,
          width: eStampDims.width,
          height: eStampDims.height,
        });

        if (employeeCnicFrontImage || employeeCnicBackImage) {
          const imagePage = pdfDoc.addPage();
          imagePage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Agreement ID: ${decryptedData.agreementId}`, { x: 130, y: 830, size: 9, color: rgb(0, 0, 0) });
          const drawWatermark = (x, y, width, height) => {
            const wmText = "MBEOWS – Agreement Use Only";
            const fontSize = 22;
            imagePage.drawText(wmText, {
              x: x + width / 4,
              y: y + height / 2,
              size: fontSize,
              rotate: degrees(28),
              color: rgb(1, 1, 1),
              opacity: 0.5,
            });
          };
          if (employeeCnicFrontImage) {
            imagePage.drawImage(employeeCnicFrontImage, { x: 40, y: 610, width: 350, height: 200 });
            drawWatermark(-10, 525, 350, 200);
            imagePage.drawText("CNIC Front", { x: 460, y: 710, size: 10, font: boldFont, color: rgb(0, 0, 0) });
          }
          if (employeeCnicBackImage) {
            imagePage.drawImage(employeeCnicBackImage, { x: 40, y: 390, width: 350, height: 200 });
            drawWatermark(-10, 305, 350, 200);
            imagePage.drawText("CNIC Back", { x: 460, y: 490, size: 10, font: boldFont, color: rgb(0, 0, 0) });
          }
        }

        const pdfBytes = await pdfDoc.save();
        saveAs(new Blob([pdfBytes], { type: 'application/pdf' }), `Agreement-${decryptedData.agreementId}.pdf`);
        toast.success('PDF Generated');
      }
      else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.log("Error generating individual agreement PDF:", err);
      toast.error(err?.response.data.message || "Failed to generate individual PDF!")
    } finally {
      setIsLoading(false);
      return;
    }
  };

  // Receipt API & Pdf Template
  const handleReceiptPdf = async (id) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/agreement/receipt/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        const customFormattedDate = formatDateTime(new Date().toISOString());

        // Load your PDF template
        const rStampUrl = '/rstamp.png';
        const rStampBytes = await fetch(rStampUrl).then((res) => res.arrayBuffer());
        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([595, 842]);
        const rStampImage = await pdfDoc.embedPng(rStampBytes);
        const rStampDims = rStampImage.scale(0.30);
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        // Receipt Section
        const receiptY = 760;

        page.drawText("PAYMENT RECEIPT", {
          x: 220,
          y: receiptY + 45,
          size: 14,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
        page.drawRectangle({
          x: 15,
          y: receiptY - 230,
          width: 565,
          height: 300,
          borderWidth: 1,
          borderColor: rgb(0, 0, 0),
        });
        page.drawText(`Service Name: Agreement (${formatLabel(decryptedData.category)}-${formatLabel(decryptedData.validFor)})`, {
          x: 40,
          y: receiptY + 10,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Service ID: ${decryptedData.agreementId}`, {
          x: 350,
          y: receiptY + 10,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Client Name: ${decryptedData.clientName || "-"}`, {
          x: 40,
          y: receiptY - 10,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Client Phone: ${decryptedData.clientPhone || "-"}`, {
          x: 350,
          y: receiptY - 10,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Amount: PKR ${formatCurrency(decryptedData.commission)}`, {
          x: 40,
          y: receiptY - 30,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Payment Status: Paid`, {
          x: 350,
          y: receiptY - 30,
          size: 11,
          color: rgb(0, 0, 0),
        });
        page.drawText(`Receipt Date: ${customFormattedDate}`, {
          x: 40,
          y: receiptY - 50,
          size: 11,
          color: rgb(0, 0, 0),
        });

        const noteLines = [
          "Important:",
          "An agreement is valid only if it contain an authentic MBEOWS assigned Agreement ID and an Approved stamp.",
          "If any of these elements are missing, the agreement shall be considered null and void."
        ];
        let noteY = receiptY - 80;
        noteLines.forEach((line, index) => {
          page.drawText(line, {
            x: 40,
            y: noteY - (index * 12),
            size: 9,
            color: rgb(0, 0, 0),
          });
        });

        page.drawText(
          "*This receipt, along with the Paid stamp, confirms payment for the above service.",
          {
            x: 60,
            y: receiptY - 200,
            size: 9,
            color: rgb(0, 0, 0),
          }
        );
        page.drawImage(rStampImage, {
          x: 460,
          y: 530,
          width: rStampDims.width,
          height: rStampDims.height,
        });

        const pdfBytes = await pdfDoc.save();
        saveAs(new Blob([pdfBytes], { type: 'application/pdf' }), `Receipt (Agreement-${decryptedData.agreementId}).pdf`);
        toast.success('Receipt Generated');
      }
      else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.log("Error generating receipt PDF:", err);
      toast.error(err?.response.data.message || "Failed to generate receipt PDF!")
    } finally {
      setIsLoading(false);
      return;
    }
  };

  // Fallback control state
  const hasViewData = Object.keys(formData1 || {}).length > 0;
  const hasUpdateData = formData2?.id;

  // Close menu list on outside click
  useEffect(() => {
    if (!menuState.open) return;
    const handleOutside = (e) => {
      const isTrigger = e.target.closest('[data-menu-trigger="true"]');
      const isMenu = e.target.closest('[data-floating-menu="true"]');
      if (isTrigger || isMenu) return;
      closeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", closeMenu);
    };
  }, [menuState.open]);

  // actions btn list
  const menuActionList = [
    {
      label: "PDF", type: "pdf", color: "text-green-600", hover: "hover:bg-green-100",
      boxType: "info", message: "Do you want to generate a PDF for this agreement, including all details if available?",
    },
    {
      label: "Receipt", type: "receipt", color: "text-emerald-600", hover: "hover:bg-emerald-100",
      boxType: "info", message: "Do you want to generate a payment receipt for this agreement?",
    },
    {
      label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
      boxType: "info", message: "Do you want to edit this agreement details?",
    },
    {
      label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
      boxType: "danger",
      message: "This will permanently delete the agreement and all associated entries. This action cannot be undone.",
    },
  ];

  return (
    <>
      <ConfirmDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.action}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        type={dialog.type}
      />
      <OpCodeDialog
        isOpen={opCodeDialog.open}
        type="danger"
        title="Process Authorization"
        message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
        confirmText="Verify"
        onClose={() =>
          setOpCodeDialog({ open: false, id: null })
        }
        onConfirm={(code) => {
          handleDelete(opCodeDialog.id, code);
        }}
      />
      {/* Three Dot Menu UI */}
      {menuState.open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div
            data-floating-menu="true"
            className="absolute bg-white border shadow-xl rounded-xl p-2 w-44 space-y-1 pointer-events-auto"
            style={{
              top: menuState.position.top,
              left: menuState.position.left,
            }}
          >
            {menuActionList.filter(action => {
              if (action.type === "receipt") {
                return menuState.rowData?.status === "active" ? true : false;
              }
              return true;
            }).map((action) => (
              <button
                key={action.type}
                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                onClick={() => {
                  closeMenu();
                  if (action.type === "pdf") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "PDF Generation",
                      message: action.message,
                      confirmText: "Generate",
                      action: () => handleIndividualPdf(menuState.rowId),
                    });
                    return;
                  }
                  if (action.type === "receipt") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Receipt Generation",
                      message: action.message,
                      confirmText: "Generate",
                      action: () => handleReceiptPdf(menuState.rowId),
                    });
                    return;
                  }
                  if (action.type === "edit") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Edit Agreement",
                      message: action.message,
                      confirmText: action.label,
                      action: () => handleUpdateOpen(menuState.rowId),
                    });
                    return;
                  }
                  setDialog({
                    open: true,
                    type: action.boxType,
                    title: "Delete Agreement",
                    message: action.message,
                    confirmText: "Proceed",
                    action: () => {
                      setOpCodeDialog({
                        open: true,
                        id: menuState.rowId,
                      });
                    },
                  });
                }}
              >
                <span className={action.color}>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Details & Update UI */}
      {isViewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Agreement Details</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.details ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasViewData ? (
                <FallBack
                  title="Agreement details not available"
                  message="This agreement record could not be loaded. It may have been removed, or there was a network issue."
                  actionText="Close"
                  onAction={handleViewClose}
                />
              ) : (
                <div className="flex-1 space-y-4 overflow-auto p-3 bg-gray-50">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-6 text-sm">
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                          ID: {formData1?.agreementId}
                        </h2>
                      </div>

                      <div>
                        <p className="text-gray-500">Category</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.category)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Ref No</p>
                        <p className="font-medium text-gray-800">{formData1?.refNo}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Valid For</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.validFor)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Agreement Date</p>
                        <p className="font-medium text-gray-800">
                          {formatDateTime(formData1?.agreementDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Expiry Date</p>
                        <p className="font-medium text-gray-800">
                          {formatDateTime(formData1?.expiryDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Name</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.clientName) || '-'}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Phone</p>
                        <p className="font-medium text-gray-800">{formData1?.clientPhone || '-'}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Client Address</p>
                        <p className="font-medium text-gray-800">{formatLabel(formData1?.clientAddress) || '-'}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Salary</p>
                        <p className="font-medium text-gray-800">PKR {formatCurrency(formData1?.salary)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Commission</p>
                        <p className="font-medium text-gray-800">PKR {formatCurrency(formData1?.commission)}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Status</p>
                        <span
                          className={`inline-flex items-center px-3 py-1 mt-1 text-xs font-medium rounded-full w-fit
                              ${formData1?.status === "pending" && "bg-blue-100 text-blue-700"}
                              ${formData1?.status === "active" && "bg-green-100 text-green-700"}
                              ${formData1?.status === "expired" && "bg-red-100 text-red-700"}
                        `}>
                          {formatLabel(formData1?.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-700">
                          {formData1?.employeeRef?.name?.charAt(0) || "E"}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {formData1?.employeeRef?.name}
                          </h3>
                          <p className="text-sm text-gray-500">Employee - Main</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">ID</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.employeeId}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">F/H</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.father_husband_name}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.employeeRef?.phone}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-xl font-bold text-indigo-700">
                          {formData1?.clientRef?.name?.charAt(0) || "C"}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {formData1?.clientRef?.name}
                          </h3>
                          <p className="text-sm text-gray-500">Client - Main</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">ID</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.clientRef?.clientId}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-800">
                            {formData1?.clientRef?.phone}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-500">Address</span>
                          <span className="font-medium text-gray-800 text-right max-w-[65%]">
                            {formData1?.clientRef?.address}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Timeline / Entries</h3>
                    {formData1?.entryRef?.map((entry, idx) => (
                      <div key={`${entry._id}-${idx}`} className="mb-3 relative">
                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 shadow-sm">
                          {/* Entry Header */}
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
                            <div>
                              <h4 className="text-sm sm:text-base font-semibold text-gray-800">
                                ID: {entry.entryId} - {formatLabel(entry.category)}
                              </h4>
                              <p className="text-gray-500 text-xs sm:text-sm">
                                {formatDateTime(entry.date)} | Type: {formatLabel(entry.type)} | Placement: {entry.placementIndex}
                              </p>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 mt-1 text-xs font-medium rounded-full w-fit
                                      ${entry.status === "pending" ? "bg-blue-100 text-blue-700" : ""}
                                      ${entry.status === "approved" ? "bg-green-100 text-green-700" : ""}
                            `}>
                              {formatLabel(entry.status)}
                            </span>
                          </div>
                          {/* Employee Info */}
                          <div className="flex items-center gap-3 mt-4">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm">
                              {entry.employeeRef?.name?.charAt(0) || "E"}
                            </div>
                            <div className="flex-1 text-sm sm:text-base">
                              <p className="font-semibold text-gray-800">{entry.employeeRef?.name}</p>
                              <p className="text-gray-500 text-xs sm:text-sm">F/H: {entry.employeeRef?.father_husband_name}</p>
                              <p className="text-gray-500 text-xs sm:text-sm">Phone: {entry.employeeRef?.phone}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {hasViewData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between">
                  <span className='text-xs'>
                    <span className="font-medium text-gray-600">Updated At:</span>{" "}
                    {formatTableDateTime(formData1.updatedAt)}
                  </span>
                  <button
                    type='button'
                    onClick={() => handleViewClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 hover:bg-blue-500 text-white font-semibold transition"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isUpdateOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Update Agreement</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.update ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasUpdateData ? (
                <FallBack
                  title="Unable to load agreement for update"
                  message="The agreement data could not be loaded for editing. Please refresh or try again."
                  actionText="Close"
                  onAction={handleUpdateClose}
                />
              ) : (
                <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                  <fieldset className="bg-white border p-4 rounded-lg">
                    <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Info</legend>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <Select
                          name="category"
                          options={categoryOptions}
                          placeholder="Select Category"
                          value={categoryOptions.find(option => option.value === formData2.category)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'category' })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Valid For</label>
                        <Select
                          name="validFor"
                          options={agreementValidOptions}
                          placeholder="Select Duration"
                          value={agreementValidOptions.find(option => option.value === formData2.validFor)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'validFor' })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                          type="datetime-local"
                          name='agreementDate'
                          value={formData2.agreementDate}
                          onChange={handleChangeUpdate}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Ref No</label>
                        <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                          placeholder="5XX"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          name='refNo'
                          value={formData2.refNo}
                          onChange={handleNumberChange}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <Select
                          name="status"
                          options={agreementStatusOptions}
                          placeholder="Select Status"
                          value={agreementStatusOptions.find(option => option.value === formData2.status)}
                          onChange={(selectedOption) =>
                            handleSelectUpdate(selectedOption, { name: 'status' })
                          }
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Salary</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                            PKR
                          </span>
                          <input
                            className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                            placeholder="XX,XXX"
                            type="text"
                            name="salary"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={formatCurrency(formData2.salary)}
                            onChange={handleNumberChange}
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Commission</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                            PKR
                          </span>
                          <input
                            className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                            placeholder="XX,XXX"
                            type="text"
                            name="commission"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={formatCurrency(formData2.commission)}
                            onChange={handleNumberChange}
                          />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              )}
              {!loaders.update && hasUpdateData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                  <button
                    type='button'
                    onClick={() => handleUpdateClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setDialog({
                      open: true,
                      title: "Confirm Update",
                      message: "Are you sure you want to update this agreement’s information? Changes will be saved immediately.",
                      confirmText: "Update",
                      type: "warning",
                      action: () => handleUpdate(formData2.id),
                    })}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Update
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* PDF Generation UI */}
      {isPdfOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Monthly Agreements</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              <div className="flex-1 overflow-auto p-3 bg-gray-50 space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Month</label>
                    <select
                      name="month"
                      value={pdfFilters.month}
                      onChange={handlePdfFilterChange}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                    >
                      <option value="">Select Month</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", { month: "long" })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Year</label>
                    <select
                      name="year"
                      value={pdfFilters.year}
                      onChange={handlePdfFilterChange}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handlePdfFetch}
                      className="w-full px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
                    >
                      Filter
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-2.5">
                  {loaders.pdfFilter ? (
                    <div className="flex justify-center items-center py-10">
                      <Loader />
                    </div>
                  ) : pdfData.length === 0 ? (
                    <p className="text-center text-gray-500 py-6">
                      No Data!
                    </p>
                  ) : (
                    <div className="max-h-[45vh] overflow-auto">
                      <table className="w-full text-sm text-center">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 border-b">ID</th>
                            <th className="p-3 border-b">Date</th>
                            <th className="p-3 border-b">Employee</th>
                            <th className="p-3 border-b">Client</th>
                            <th className="p-3 border-b">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pdfData.map((item, index) => (
                            <tr key={index} className="even:bg-gray-50">
                              <td className="p-3 border-b">{item.agreementId}</td>
                              <td className="p-3 border-b">{formatTableDateTime(item.agreementDate)}</td>
                              <td className="p-3 border-b">{item.employee?.name}</td>
                              <td className="p-3 border-b">{item.client?.name}</td>
                              <td className="p-3 border-b">{formatLabel(item.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                <button
                  onClick={handlePdfClose}
                  className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                >
                  Close
                </button>
                {pdfData.length > 0 && (
                  <button
                    onClick={() => {
                      setDialog({
                        open: true,
                        type: "info",
                        title: "PDF Generation",
                        message: "Do you want to generate the PDF for the displayed agreements?",
                        confirmText: "Generate",
                        action: () => handleMonthlyPdf(),
                      })
                    }}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition bg-green-600 text-white hover:bg-green-700"
                  >
                    Generate
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        {/* Heading */}
        <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              className="md:hidden p-2 bg-white shadow rounded-lg"
              onClick={() => setIsMobileOpen(true)}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
            </button>
            <div className="flex flex-wrap items-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                Agree
              </h1>
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                ment
              </span>
            </div>
          </div>

          <div>
            <button
              className="mr-2 px-4 sm:px-6 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 font-medium text-sm sm:text-base"
              onClick={handlePdfOpen}
            >
              PDF
            </button>
          </div>
        </div>

        <hr className="border-t border-gray-200 my-4" />

        {/* Data Display Code */}
        <div className='w-full'>
          {/* Quick & Deep Search Bar */}
          <div className="w-full flex justify-center mb-7">
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-full max-w-2xl flex items-center"
            >
              {/* Search Input */}
              <div className="relative flex-1">
                <input
                  type="search"
                  placeholder={`${searchMode === "quick" ? "Quick" : "Deep"} search . . .`}
                  value={query}
                  onChange={handleQuickSearch}
                  className="w-full py-3 pl-10 sm:pl-12 pr-32 sm:pr-40 rounded-2xl bg-white/80 backdrop-blur-sm 
                             border border-gray-200 shadow-md focus:ring-2 focus:ring-indigo-400 
                             focus:outline-none text-gray-700 placeholder-gray-400 transition"
                />
                <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              {/* Mode Toggle */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex rounded-full overflow-hidden border border-gray-200 shadow">
                <button
                  type="button"
                  onClick={() => setSearchMode("quick")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "quick"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Quick
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode("deep")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "deep"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Deep
                </button>
              </div>
            </form>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto h-[70vh]">
            {(loaders.fetch || loaders.search) ? (
              <div className="flex justify-center items-center h-full p-6">
                <Loader />
              </div>
            ) : displayData.length === 0 ? (
              <div className="flex justify-center items-center h-full p-6">
                <p className="font-semibold text-gray-600 text-xl sm:text-md">
                  {searchMode === "quick" && query.trim()
                    ? "No Match !!"
                    : searchMode === "deep"
                      ? "No Records !!"
                      : "No Agreement !!"}
                </p>
              </div>
            ) : (
              <>
                <table className="w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "#",
                        "ID",
                        "Date",
                        "Category",
                        "Employee",
                        "Client",
                        "Status",
                        "Created",
                        "Action",
                      ].map((header, index) => (
                        <th
                          key={index}
                          className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((data, index) => (
                      <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.agreementId}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.agreementDate)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatLabel(data.category)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.employee}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.client}
                        </td>
                        <td className="p-3 text-sm text-center border-b border-gray-200">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full w-fit
                              ${data?.status === "pending" && "bg-blue-100 text-blue-700"}
                              ${data?.status === "active" && "bg-green-100 text-green-700"}
                              ${data?.status === "expired" && "bg-red-100 text-red-700"}
                          `}>
                            {formatLabel(data.status)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.createdAt)}
                        </td>
                        <td className="p-3 text-center border-b border-gray-200">
                          <div className="flex justify-center space-x-2">
                            <button
                              type='button'
                              className="px-2 sm:px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-medium text-sm sm:text-base"
                              onClick={() => {
                                setDialog({
                                  open: true,
                                  type: "info",
                                  title: "View Details",
                                  message: "Want to view details for this agreement?",
                                  confirmText: "View",
                                  action: () => handleViewOpen(data._id),
                                })
                              }}
                            >
                              Details
                            </button>

                            <button
                              type="button"
                              data-menu-trigger="true"
                              className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (menuState.open && menuState.rowId === data._id) {
                                  closeMenu();
                                  return;
                                }
                                const rect = e.currentTarget.getBoundingClientRect();
                                openMenu({
                                  id: data._id,
                                  row: data,
                                  position: {
                                    top: rect.bottom + window.scrollY - (data?.status === "active" ? 220 : 180),
                                    left: rect.right + window.scrollX - 180,
                                  },
                                });
                              }}
                            >
                              <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

          </div>

          {/* Pagination */}
          {searchMode === "quick" && totalPages > 1 && (
            <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
              {/* Showing Entries Text */}
              <span className="text-sm text-gray-600 text-center sm:text-left">
                Showing {startIndex} to {endIndex} of {totalBackendPg} entries
              </span>
              {/* Pagination Buttons */}
              <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={loaders.fetch || currentPage === 1}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                {/* Page Numbers */}
                {(() => {
                  const buttons = [];
                  const pageRange = 1; // current ±2 pages
                  let startPage = Math.max(currentPage - pageRange, 1);
                  let endPage = Math.min(currentPage + pageRange, totalPages);

                  if (startPage > 1) {
                    buttons.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    buttons.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                    buttons.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return buttons;
                })()}
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={loaders.fetch || currentPage === totalPages}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Agreement;